import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCommentDots, faInfoCircle, faHandsHelping, faSitemap, faCreditCard, faSlidersH, faInfo } from '@fortawesome/free-solid-svg-icons';
import { useSlideSequenceQuery } from '@queries/scenarios';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { SlideList, SlideListItem, RemoveSlideFromList } from '@slides/styles';
import { useUser } from '@context/user';
import { usePresentation } from '@context/presentation';
import { updateCurrentSlide } from '@utils/currentSlide';
import { useSlideSequenceMutation } from '@mutations/scenarios';
import { getErrorMessage } from '@utils/errorHandling';
import { UNAVAILABLE_CS_FIRM, Profile, SLIDES_WITH_CREDIT_DATA } from '@constants';

// To-do: add icons for rebuttals
const icons = {
    'Introduction': faCommentDots,
    'Your Analysis': faInfoCircle,
    'We Can Help': faHandsHelping,
    'Term Options': faSitemap,
    'Modification': faCreditCard,
    'Summary': faSlidersH,
    'Additional Info': faInfo
};

export const DraggableSlideSequence = ({ dispatch, slideId }) => {
    const { scenarioId } = useUser();
    // Makes more sense to grab profile off the user object, but that makes it impossible for Admins/QA
    // to test CS Profile (since they can't change their assigned profile)
    const { profile, presentationId, client } = usePresentation();
    const { firm } = client;
    const { error, isLoading, data: slideSequence } = useSlideSequenceQuery(scenarioId);
    const slideSequenceMutation = useSlideSequenceMutation();

    const selectSlide = async (id) => {
        await updateCurrentSlide(dispatch, id, slideId, presentationId, profile);
    };

    const rearrangeSlides = (result) => {
        // Prevents user from dropping a slide outside of the droppable area
        if (!result.destination) return;

        // Remove the dragged slide from its original place in the array
        const slides = [...slideSequence];
        const [reorderedSlide] = slides.splice(result.source.index, 1);

        // Add the dragged slide to the new position in the array and reset the UI state
        slides.splice(result.destination.index, 0, reorderedSlide);

        slideSequenceMutation.mutate({ sequence: slides, isDefault: false, scenarioId });
    };

    const removeSlide = (id, evt) => {
        const newSequence = slideSequence.filter(slide => slide.id !== id);

        slideSequenceMutation.mutate({ sequence: newSequence, isDefault: false, scenarioId });

        // Prevent bubbling up to other click handlers
        evt.stopPropagation();
    };

    const RemoveFromSequence = ({ id }) => (
        <RemoveSlideFromList onClick={evt => removeSlide(id, evt)}>
            x
        </RemoveSlideFromList>
    );

    if (isLoading) return <div>Loading slide sequence...</div>;

    if (error) return <div>An error occurred while fetching the slides: {getErrorMessage(error)}</div>;
    
    const lawFirmNotAvailable = firm === UNAVAILABLE_CS_FIRM;
    const isDragDisabled = profile === Profile.CUSTOMER_SERVICE;
    const isSlideDisabled = (id) => lawFirmNotAvailable && SLIDES_WITH_CREDIT_DATA.includes(parseInt(id));

    return (
        <DragDropContext onDragEnd={rearrangeSlides} >
            <Droppable droppableId='slide-list' direction='horizontal'>
                { (provided) => (
                    <SlideList {...provided.droppableProps} ref={provided.innerRef} data-testid='slide-list'>
                        { 
                            slideSequence.map(({name, id, isRequired}, index) => {
                                return (
                                    <Draggable isDragDisabled={isDragDisabled} key={id} draggableId={id.toString()} index={index}>
                                        {
                                            (provided) => (
                                                <SlideListItem
                                                    {...provided.draggableProps} 
                                                    {...provided.dragHandleProps}
                                                    ref={provided.innerRef}
                                                    selected={slideId === id}
                                                    data-testid='slide-thumbnail'
                                                    disabled={isSlideDisabled(id)}
                                                    onClick={() => { !isSlideDisabled(id) && selectSlide(parseInt(id))}}
                                                    className='slide-menu-item'>
                                                        {!isRequired && <RemoveFromSequence id={id} />}
                                                        {icons[name] && <FontAwesomeIcon icon={icons[name]} />}
                                                        {name}
                                                </SlideListItem>
                                            )
                                        }
                                    </Draggable>
                                );
                            })
                        }
                        {provided.placeholder}
                    </SlideList>
                )}
            </Droppable>
        </DragDropContext>
    );
};

export default DraggableSlideSequence;
